import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  DocumentTextIcon,
  PhotoIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  PencilSquareIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

export default function MobileDocumentCard({ document }) {
  const getStatusBadge = () => {
    if (document.has_error) {
      return (
        <span className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 bg-red-500/20 text-red-400 text-xs font-medium rounded-full">
          <ExclamationCircleIcon className="w-3 h-3" />
          Error
        </span>
      );
    }
    if (document.is_processed) {
      return (
        <span className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded-full">
          <CheckCircleIcon className="w-3 h-3" />
          Done
        </span>
      );
    }
    return (
      <span className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs font-medium rounded-full">
        <ClockIcon className="w-3 h-3" />
        Pending
      </span>
    );
  };

  const getFileIcon = () => {
    if (document.file_type === 'pdf') {
      return <DocumentTextIcon className="w-10 h-10 text-red-400" />;
    }
    return <PhotoIcon className="w-10 h-10 text-blue-400" />;
  };

  // Get thumbnail URL
  const getThumbnail = () => {
    if (document.pages && document.pages.length > 0 && document.pages[0].image) {
      const img = document.pages[0].image;
      if (img.startsWith('http')) return img;
      return `${import.meta.env.VITE_API_URL || ''}${img.startsWith('/') ? '' : '/'}${img}`;
    }
    if (document.thumbnail) {
      const img = document.thumbnail;
      if (img.startsWith('http')) return img;
      return `${import.meta.env.VITE_API_URL || ''}${img.startsWith('/') ? '' : '/'}${img}`;
    }
    return null;
  };

  const thumbnail = getThumbnail();
  const docPath = `/documents/${document.slug || document.id}`;
  const cropPath = `${docPath}/crop`;

  return (
    <motion.div
      whileTap={{ scale: 0.98 }}
      className="bg-theme-secondary rounded-2xl overflow-hidden border border-theme-primary shadow-sm"
    >
      {/* Thumbnail — links to document detail (View) */}
      <Link to={docPath} className="block">
        <div className="relative aspect-[4/3] bg-theme-tertiary">
          {thumbnail ? (
            <img
              src={thumbnail}
              alt={document.title}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              {getFileIcon()}
            </div>
          )}
          {getStatusBadge()}
        </div>

        {/* Title + meta */}
        <div className="px-3 pt-3 pb-2">
          <h3 className="text-sm font-medium text-theme-primary truncate">
            {document.title || 'Untitled'}
          </h3>
          <div className="flex items-center justify-between mt-1">
            <p className="text-xs text-theme-muted">
              {document.pages_count ?? document.pages?.length ?? 0} pages
            </p>
            <p className="text-xs text-theme-muted">
              {document.created_at ? new Date(document.created_at).toLocaleDateString() : '-'}
            </p>
          </div>
        </div>
      </Link>

      {/* Action row — Edit (crop) + View */}
      <div className="flex items-center gap-1 px-3 pb-3 pt-1 border-t border-theme-primary mt-1">
        <Link
          to={cropPath}
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium text-theme-muted hover:text-blue-400 hover:bg-blue-500/10 transition-colors"
          title="Edit pages"
        >
          <PencilSquareIcon className="w-4 h-4" />
          Edit
        </Link>
        <div className="w-px h-5 bg-theme-primary" />
        <Link
          to={docPath}
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium text-theme-muted hover:text-brand-orange hover:bg-brand-orange/10 transition-colors"
          title="View document"
        >
          <EyeIcon className="w-4 h-4" />
          View
        </Link>
      </div>
    </motion.div>
  );
}
